import { setAllAnalyticsIdentifiersFromQuery } from '$lib/services/analytics/identifiers';
import { ApiService } from '$lib/services/api';
import { app } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import {
	getJSPath,
	hashElementPath,
	isCustomTrackedElement,
	trackedElems,
} from '$lib/utils/click-tracking';
import {
	COOKIES,
	COOKIES_CONSENT,
	EVENT_NAMES,
	LOCAL_STORAGE_KEYS,
	QUERY_PARAM_KEYS,
} from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import * as Sentry from '@sentry/sveltekit';
import Cookies from 'js-cookie';
import { get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { ProductExperienceService } from '../product-experience';

/**
 *
 */
class AnalyticsService {
	/**
	 * Handles click tracking for analytics.
	 * @param {Event} e - The click event.
	 * @returns {Promise<object>} - A promise that resolves when the event is tracked.
	 */
	static async clickTrackingHandler(e) {
		e.stopPropagation();

		try {
			let targetElement = e.target;

			// Traverse up the DOM to find the closest tracked element
			while (targetElement && targetElement !== document) {
				if (trackedElems.includes(targetElement.tagName) || isCustomTrackedElement(targetElement)) {
					break;
				}
				targetElement = targetElement.parentElement;
			}

			// If no tracked element is found, exit the handler
			if (!targetElement || targetElement === document) return;

			const event = await AnalyticsService.createClickEvent(targetElement);

			return await AnalyticsService.fireEvent({ event });
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					action: 'Error: Click Tracking Handler',
				},
			});
			console.error(e);
		}
	}

	/**
	 * Logs an event to the server without relying on the store or the API.
	 * @param {object} payload - The payload to log.
	 * @param {string} deviceId - The device id to log.
	 * @param {boolean} preserveSessionDeviceId - Whether to preserve the session device id.
	 * @param {string | null} token - The token to use for the request headers.
	 * @returns {Promise<object>} - A promise that resolves when the event is logged.
	 */
	static async statelessEventLog(
		payload,
		deviceId = 'client_failure',
		preserveSessionDeviceId = true,
		token = ApiService.getToken()
	) {
		try {
			if (payload) {
				payload.app_version = __APP_VERSION__; //eslint-disable-line
			}

			return await ApiService.eventLog(
				{
					event: {
						event_type: EVENT_NAMES.DEBUG,
						event_payload: payload,
					},
				},
				window.fetch,
				(payload) =>
					ApiService.statelessPack(payload, { deviceId, preserveSessionDeviceId, aoflToken: token })
			);
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					action: 'Error: Fire Event Log (stateless)',
				},
			});
		}
	}

	/**
	 * Create a click event
	 * @param {HTMLElement} targetElement - the element that was clicked
	 * @returns {Promise<{event_type: string, device_id: (string|*), event_payload: {aofl_product: string, user_to_product_uuid: (*|string), link_info: {full_url: string, device_info: (*&{user_agent: string | undefined}), client_experiments: (string|string), track_cookie: ({[p: string]: string}|string), link_id: *, app_version: *, element_path: string, page_name: string, site_section: string}, url_variable_info: {}, udid: (string|undefined), adid: (string|undefined)}}>}
	 */
	static async createClickEvent(targetElement) {
		const pageName = AnalyticsService.createPageName();
		const elementPath = getJSPath(targetElement);
		let linkId = isCustomTrackedElement(targetElement);

		if (!linkId) {
			const hashedPath = await hashElementPath(elementPath);
			linkId = hashedPath.slice(0, 8);
		}

		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const firstVisibleElement = AnalyticsService.getFirstVisibleElement(
			'main > [slot="content"]',
			80
		);

		const event = {
			event_type: EVENT_NAMES.LINK_CLICK,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				link_info: {
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					link_id: linkId,
					app_version: __APP_VERSION__, //eslint-disable-line
					element_path: elementPath,
					page_name: pageName,
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					site_section: firstVisibleElement?.dataset.sectionName || 'regpath',
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		return event;
	}

	/**
	 * @returns {string} The created page name.
	 */
	static createPageName() {
		if (location.pathname === '/') return 'home';
		return location.pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '-');
	}

	/**
	 * @param {string} userAgent - The user agent string to check.
	 * @returns {boolean|null} True if the user agent string includes 'isAutomatedTesting', false otherwise, null if the user agent is not a string.
	 */
	static isAutomatedTestSession(userAgent) {
		if (!userAgent || typeof userAgent !== 'string') return null;
		return userAgent.includes('isAutomatedTesting');
	}

	/**
	 * Sets up a click tracking listener on the document.
	 * @returns {void} A promise that resolves when the listener is set up.
	 */
	static setClickTrackingListener() {
		document.addEventListener('click', AnalyticsService.clickTrackingHandler);
	}

	/**
	 * Removes the app navigation listeners.
	 */
	static removeAppNavigationListeners() {
		const visibilityChangeEvent =
			'onvisibilitychange' in document ? 'visibilitychange' : 'pagehide';
		document.removeEventListener(visibilityChangeEvent, AnalyticsService.visibilityChangeHandler);
	}

	/**
	 * Removes the click tracking listener.
	 */
	static removeClickTrackingListener() {
		document.removeEventListener('click', AnalyticsService.clickTrackingHandler);
	}

	/**
	 * Tracks a client validation event.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackClientValidation() {
		try {
			const appMeta = get(app)?.meta || {};
			const userToProductUuid = get(user)?.userToProductUuid || '';
			const event = {
				event_type: EVENT_NAMES.CLIENT_VALIDATION,
				device_id: getDeviceId(),
				event_payload: {
					aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
					user_to_product_uuid: userToProductUuid,
					data: {
						full_url: window.location.href,
						device_info: {
							...appMeta,
							user_agent: window.navigator?.userAgent,
							platform: window.navigator?.platform,
							vendor: window.navigator?.vendor,
							max_touch_points: window.navigator?.maxTouchPoints,
						},
						client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
						experiment_id: Cookies.get(COOKIES.EXPERIMENTS),
						track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
						site_section: 'regpath',
						is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
						page_url: location.origin + location.pathname,
					},
					url_variable_info: AnalyticsService.getUrlVariableInfo(),
					udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
					adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
				},
			};

			return await AnalyticsService.fireEvent({ event });
		} catch (error) {
			Sentry.captureException(error, {
				extra: { action: 'Error: Tracking Client Validation' },
			});
		}
	}

	/**
	 * Tracks a client validation event.
	 * @param {object} payload - The payload to track.
	 * @param {object} payload.form - The form data to track.
	 * @param {string} payload.component - The component to track.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackClientServerSideValidation(payload) {
		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const { form, component } = payload;
		const event = {
			event_type: EVENT_NAMES.CLIENT_VALIDATION,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				data: {
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					experiment_id: Cookies.get(COOKIES.EXPERIMENTS),
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: 'regpath',
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					page_url: location.origin + location.pathname,
					form,
					component,
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks experiment assignment events for analytics.
	 * @param {object} options - The options for tracking the experiment assignment
	 * @param {string} [options.customPageName] - Optional custom page name to override default
	 * @param {string} [options.rotationId] - The ID of the rotation/experiment being tracked
	 * @returns {Promise<object>} A promise that resolves when the event is tracked
	 */
	static async trackExperimentAssignment(options = {}) {
		// Get page name from options or generate default
		const { customPageName } = options;
		const pageName = customPageName ?? AnalyticsService.createPageName();

		// Extract URL search params into object
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		// Get app metadata from store
		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';

		// Construct the experiment assignment event
		const event = {
			event_type: EVENT_NAMES.EXPERIMENT_ASSIGNMENT,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				rotation_info: {
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					rotation_id: options.rotationId,
					site_section: 'regpath',
					page_name: pageName,
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					page_url: location.origin + location.pathname,
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		// Fire the event through analytics service
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * @param {string} metricName - The name of the metric to track.
	 * @param {object} data - The data to track.
	 * @returns {Promise<object>}
	 */
	static async trackClientDebugMetric(metricName, data) {
		return await AnalyticsService.trackClientDebug({
			eventSubType: 'metric',
			metricName,
			data: data,
		});
	}

	/**
	 * Tracks a paddle checkout event.
	 * @param {object} [options] - The optional options object.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackPaddleCheckoutEvent(options = {}) {
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		const appMeta = get(app)?.meta || {};
		const event = {
			event_type: EVENT_NAMES.PADDLE_CHECKOUT_EVENT,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				data: {
					paddle_checkout_info: { ...options },
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					experiment_id: Cookies.get(COOKIES.EXPERIMENTS),
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: 'regpath',
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					page_url: location.origin + location.pathname,
				},
				url_variable_info: params,
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks a client validation event.
	 * @param {object} [options] - The optional options object.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackClientDebug(options = {}) {
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.DEBUG,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				data: {
					debug_info: { ...options },
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: 'regpath',
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					page_url: location.origin + location.pathname,
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};

		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * @param {object} options - The options for tracking the third party conversion
	 * @param {?string} options.conversion_event_id - The conversion event id
	 * @param {?string} options.country_code - The country code
	 * @param {?string} options.email - The email
	 * @param {?object} options.event_info - The event info
	 * @param {?Array<string>} options.event_source - The event source
	 * @param {string} options.event_type - The event type
	 * @param {?string} [options.user_to_product_uuid] - The user to product uuid
	 * @returns {Promise<void>} A promise that resolves when the conversion is tracked
	 */
	static async trackThirdPartyConversion({
		conversion_event_id,
		country_code,
		email,
		event_info,
		event_source,
		event_type,
		user_to_product_uuid,
	}) {
		if (get(app)?.meta?.inApp) return;

		try {
			const payload = {
				conversion_event_id: conversion_event_id || uuidv4(),
				country_code: country_code || 'US',
				event_info: event_info || {},
				event_source: event_source || [],
				event_source_url: 'https://www.abcmouse.com',
				event_type: event_type,
				hashed_email: email ? await AnalyticsService.hashEmailAddress(email) : '',
				user_agent: window.navigator?.userAgent,
				platform: window.navigator?.platform,
				vendor: window.navigator?.vendor,
				max_touch_points: window.navigator?.maxTouchPoints,
				user_to_product_uuid: user_to_product_uuid || '',
			};

			await AnalyticsService.trackClientDebug({
				...payload,
				debug: 'third_party_conversion_call',
			});

			return await ApiService.logThirdPartyConversion(payload);
		} catch (err) {
			Sentry.captureException(err, {
				extra: { action: 'Error: Track Third Party Conversion' },
			});
		}
	}

	/**
	 * Tracks a page view event.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.customPageName] - An optional custom page name.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackPageView(options = {}) {
		const { customPageName, componentName } = options;
		const pageName = customPageName ?? AnalyticsService.createPageName();
		// for 1.0-2.0 rotation
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.PAGE_LOAD,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				page_info: {
					full_url: window.location.href,
					device_info: {
						...appMeta,
						device_id_from_doc_cookie: document?.cookie
							?.split(';')
							.filter((e) => e.includes('device_id'))
							.join('')
							.trim(),
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: 'regpath',
					page_name: pageName,
					component_name: componentName,
					page_url: location.origin + location.pathname,
					view_info: [],
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
				},
				ssr_context: !!ApiService.serverSideContext,
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				referrer: document?.referrer ? document?.referrer : '',
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks a ui view event.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.customPageName] - An optional custom /view name.
	 * @param {string} [options.elementName] - An optional element name.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackUIShow(options = {}) {
		const { customPageName, elementName } = options;
		const pageName = customPageName ?? AnalyticsService.createPageName();
		const userToProductUuid = get(user)?.userToProductUuid || '';

		const event = {
			event_type: EVENT_NAMES.UI_SHOW,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				page_info: {
					page_name: pageName,
					page_url: location.origin + location.pathname,
					site_section: 'regpath',
					view_info: [],
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
				},
				element_name: elementName ?? 'modal-popup',
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				referrer: document?.referrer ? document?.referrer : '',
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks a QR code visited event.
	 * @param {string} token - The QR code token.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.customPageName] - An optional custom page name.
	 * @param {string} [options.source] - The source of the QR code (defaults to 'qr').
	 * @param {string} [options.siteSection] - The site section (defaults to 'qr-entry').
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackQrCodeVisited(token, options = {}) {
		const { customPageName, source = 'qr', siteSection = 'qr-entry', componentName } = options;
		const pageName = customPageName ?? AnalyticsService.createPageName();

		const appMeta = get(app)?.meta || {};
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.QR_CODE_VISITED,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				page_info: {
					full_url: window.location.href,
					device_info: {
						...appMeta,
						user_agent: window.navigator?.userAgent,
						platform: window.navigator?.platform,
						vendor: window.navigator?.vendor,
						max_touch_points: window.navigator?.maxTouchPoints,
					},
					client_experiments: localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) ?? 'N/A',
					track_cookie: Cookies.get(COOKIES.TRACK_COOKIE) || 'none',
					site_section: siteSection,
					page_name: pageName,
					component_name: componentName,
					page_url: location.origin + location.pathname,
					view_info: [],
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
				},
				qr_info: {
					site_section: siteSection,
					source: source,
					token: token,
				},
				url_variable_info: AnalyticsService.getUrlVariableInfo(),
				referrer: document?.referrer ? document?.referrer : '',
				udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
				adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 * Tracks a link entry event.
	 * @param {object} [options] - The optional options object.
	 * @param {string} [options.source] - The source.
	 * @param {string} [options.type] - The type.
	 * @returns {Promise<object>} A promise that resolves when tracking completes.
	 */
	static async trackLinkEntry(options = {}) {
		const { source, type } = options;
		const userToProductUuid = get(user)?.userToProductUuid || '';
		const event = {
			event_type: EVENT_NAMES.LINK_ENTRY_VISITED,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				link_info: {
					source: source,
					type: type,
				},
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 *
	 * Tracks an email click event if email click query param is present.
	 * @returns {Promise<void>} A promise that resolves when the tracking is complete.
	 */
	static async trackEmailClick() {
		const urlSearchParam = new URLSearchParams(location.search);
		const emailClickParam = urlSearchParam.get(QUERY_PARAM_KEYS.EMAIL_CLICK);

		if (!emailClickParam || typeof emailClickParam !== 'string') return;

		const payload = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			request_url: location.href,
		};

		try {
			await ApiService.receipientLogEmailClick(payload);
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Analytics Service: Track email click',
				},
			});
		}
	}

	/**
	 * Tracks an account settings update event.
	 * @param {object} [options] - The optional options object.
	 * @param {boolean} [options.email] - The email address.
	 * @param {boolean} [options.password] - The first name.
	 * @returns {Promise<object>} A promise that resolves when the tracking is complete.
	 */
	static async trackAccountSettingsUpdate(options = {}) {
		const { email, password } = options;
		const userToProductUuid = get(user)?.userToProductUuid || '';

		const event = {
			event_type: EVENT_NAMES.ACCOUNT_SETTINGS_UPDATE,
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				user_to_product_uuid: userToProductUuid,
				settings_updated: {
					email,
					password,
				},
			},
		};
		return await AnalyticsService.fireEvent({ event });
	}

	/**
	 *
	 * Fires internal pixels if email click query param is present.
	 * @param {string} event - The event to fire.
	 * @param {object} replacements - The replacements for the event.
	 * @returns {Promise<void>} A promise that resolves when the tracking is complete.
	 */
	static async fireInternalPixels(event, replacements = {}) {
		const { pixelsInternal } = await import('./pixels');

		if (pixelsInternal[event]) {
			const internalPixels = pixelsInternal[event];

			for (let i = 0; i < internalPixels.length; i++) {
				for (const key in replacements) {
					if (replacements.hasOwnProperty(key)) {
						const regex = new RegExp('{{' + key + '}}', 'gi');
						internalPixels[i] = internalPixels[i].replace(regex, replacements[key]);
					}
				}

				try {
					await fetch(internalPixels[i], {
						method: 'GET',
						headers: {
							'Content-type': 'application/json;charset=UTF-8',
							'Access-Control-Allow-Origin': '*',
						},
						mode: 'no-cors',
					});
				} catch (err) {
					Sentry.captureException(err, {
						extra: {
							action: 'Error: Analytics Service: Fire Internal Pixels',
						},
					});
				}
			}
		}
	}

	/**
	 * @returns {void}
	 */
	static thirdPartyPageLoad() {
		const page = AnalyticsService.createPageName();
		try {
			// Google Analytics
			if (window.gtag) {
				window.gtag('set', 'page', page);
				window.gtag('send', 'pageview');
			}

			// Bing UET
			window.uetq = window.uetq || [];
			window.uetq.push('event', 'page_view', { page_path: page });
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Third Party Page Load',
				},
			});
		}
	}

	/**
	 * @returns {object} The URL variable info
	 */
	static getUrlVariableInfo() {
		const searchParams = new URLSearchParams(location.search);
		const params = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}
		return params;
	}

	/**
	 * Get the hashed email address depending on the source tag and recipient domain
	 * Google requires specific email normalization for their conversion tracking
	 * @param {string} email - the email address to hash
	 * @returns {Promise<string>} A promise that resolves to the hashed email address.
	 */
	static async hashEmailAddress(email) {
		const sourceTag = AnalyticsService.getSourceTag();
		const normalizedEmail = sourceTag?.includes('goog')
			? AnalyticsService.normalizeGoogleEmail(email)
			: AnalyticsService.normalizeEmail(email);
		return await AnalyticsService.hashValue(normalizedEmail);
	}

	/**
	 * Normalizes an email address by trimming whitespace and converting to lowercase.
	 * @param {string} email - The email address to normalize.
	 * @returns {string} The normalized email address.
	 */
	static normalizeEmail(email) {
		return email.trim().toLowerCase();
	}

	/**
	 * Normalizes a Google email address by removing dots from the local part for gmail/googlemail domains.
	 * @param {string} email - The email address to normalize.
	 * @returns {string} The normalized email address.
	 */
	static normalizeGoogleEmail(email) {
		const normalized = AnalyticsService.normalizeEmail(email);
		const [localPart, domain] = normalized.split('@');
		const isGoogleDomain = domain && /^(gmail|googlemail)\.com\s*$/.test(domain);
		const cleanLocalPart = isGoogleDomain ? localPart.replace(/\./g, '') : localPart;
		return `${cleanLocalPart}@${domain}`;
	}

	/**
	 * @param {string} string string to hash
	 * @returns {Promise<string>} A promise that resolves to the hashed string
	 */
	static async hashValue(string) {
		const utf8 = new TextEncoder().encode(string);
		const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
		return hashHex;
	}

	/**
	 * Sends a beacon with the provided data.
	 * @param {object} data - The data to be sent.
	 * @returns {boolean} - Returns true if the beacon was successfully sent, otherwise false.
	 */
	static async sendBeacon(data) {
		const payload = ApiService.pack(data);
		return navigator.sendBeacon(`/ws/amsl/0.1/json/Event/Log/init`, payload);
	}

	/**
	 * Parses a full name into firstname and lastname components.
	 * @param {string} fullName - The full name to parse (e.g., "John Doe" or "Mary Jane Smith")
	 * @returns {{ firstname: string, lastname: string }} - Object containing firstname and lastname
	 */
	static parseFullName(fullName) {
		if (!fullName || typeof fullName !== 'string') {
			return { firstname: '', lastname: '' };
		}

		const trimmedName = fullName.trim();
		if (!trimmedName) {
			return { firstname: '', lastname: '' };
		}

		const nameParts = trimmedName.split(/\s+/);

		if (nameParts.length === 1) {
			// Single name provided
			return { firstname: nameParts[0], lastname: '' };
		}

		const firstname = nameParts[0];
		const lastname = nameParts.slice(1).join(' ');

		return { firstname, lastname };
	}

	/**
	 * Strips phone number formatting to return only digits.
	 * Rokt expects phone numbers in format: 1234567890 or +11234567890
	 * @param {string} phone - The phone number (e.g., "(123) 456-7890")
	 * @returns {string} - Phone with only digits
	 */
	static formatPhoneForGTM(phone) {
		if (!phone || typeof phone !== 'string') {
			return '';
		}

		return phone.replace(/\D/g, '');
	}

	/**
	 * Pushes data to the dataLayer for analytics tracking.
	 * @param {object} data - The data to be pushed to the dataLayer.
	 * @param {string} [data.currency] - The currency tied to the event(optional).
	 * @param {string} [data.email] - The hashed email of the user (optional).
	 * @param {string} [data.espRecipientID] - The ID of the email service provider recipient (optional).
	 * @param {string} data.eventName - The name of the event.
	 * @param {string} [data.event_id] - The ID of the event.
	 * @param {string} [data.fbc] - The facebook click ID (optional).
	 * @param {string} [data.fbp] - The facebook pixel ID (optional).
	 * @param {string} [data.firstname] - The user's first name (optional).
	 * @param {string} [data.language] - The language code (e.g., 'en', 'es') (optional).
	 * @param {string} [data.lastname] - The user's last name (optional).
	 * @param {string} [data.ltv] - The predicted lifetime value of the event initiator (optional).
	 * @param {string} [data.mobile] - The user's mobile phone number (optional).
	 * @param {string} [data.product] - The hash of the subscription plan associated with the event (optional).
	 * @param {string} [data.state] - The state of the user (optional).
	 * @param {string} [data.user_to_product_uuid] - The user to product uuid of the event initiator (optional).
	 * @param {string} [data.value] - The actual conversion amount paid at transaction (optional).
	 * @param {string} [data.zip] - The user's zip code from billing address (optional, CC only).
	 */
	static async dataLayerPush(data) {
		if (get(app)?.meta?.inApp) return;

		let experimentInfo = {};
		try {
			window.dataLayer = window.dataLayer || [];
			const appMeta = get(app)?.meta;
			const appGlobal = get(app)?.global || {};

			let deviceType = 'Desktop';
			if (appMeta.isIosApp || appMeta.isIosViewer) {
				deviceType = 'iOS';
			} else if (appMeta.isAndroidApp || appMeta.isAndroidViewer) {
				deviceType = 'Android';
			}

			try {
				const experimentsJson = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
				experimentInfo = JSON.parse(experimentsJson) || {};
			} catch (err) {
				Sentry.captureException(err, {
					extra: {
						action: 'Error: Decoding Experiment Info',
					},
				});
			}
			window.dataLayer.push({
				id: data?.event_id || uuidv4(),
				event: data?.eventName,
				eventData: {
					analyticsCookiesEnabled:
						appGlobal.analyticsCookiesEnabled || Cookies.get(COOKIES_CONSENT.ANALYTICS) === 'true',
					cjeCookie: Cookies.get(COOKIES.CJ_COOKIE) ?? '',
					clientVersion: __APP_VERSION__, //eslint-disable-line
					currency: data?.currency || 'USD',
					deviceType,
					espRecipientID: data?.espRecipientID ?? '',
					experimentInfo,
					externalID: data?.user_to_product_uuid
						? await AnalyticsService.hashValue(data?.user_to_product_uuid)
						: '',
					fbc: data?.fbc,
					fbp: data?.fbp,
					firstname: data?.firstname ?? '',
					geo: appMeta.viewerCountry,
					hashedEMail: data?.email ? await AnalyticsService.hashEmailAddress(data?.email) : '',
					inApp: appMeta.inApp,
					is_automated_test: AnalyticsService.isAutomatedTestSession(window.navigator?.userAgent),
					language: data?.language ?? '',
					lastname: data?.lastname ?? '',
					ltv: data?.ltv,
					ltvCurrency: 'USD', // LTV always in USD
					mobile: data?.mobile ?? '',
					pageUrl: location.href,
					product: data?.product,
					sourceTag: AnalyticsService.getSourceTag(),
					state: data?.state ?? '',
					targetingCookiesEnabled:
						appGlobal.targetingCookiesEnabled || Cookies.get(COOKIES_CONSENT.TARGETING) === 'true',
					timestamp: Date.now(),
					userAgent: navigator.userAgent,
					value: data?.value ?? '',
					zip: data?.zip ?? '',
				},
			});
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error: Data Layer Push',
				},
			});
		}
	}

	/**
	 * Retrieves the source tag from the URL search parameters or local storage.
	 * If a sourceTag exists, stores other query parameters in localStorage along with the tag itself.
	 * If legacySourceTag exists, checks the dynamic info cookie and stores in localStorage along with the tag itself.
	 * @returns {string|null} The source tag or null if not found.
	 */
	static getSourceTag() {
		const searchParams = new URLSearchParams(location.search);
		const sourceTag = searchParams.get(QUERY_PARAM_KEYS.SOURCE_TAG);

		const legacyCookiesNames = [
			COOKIES.LEGACY_CAMPAIGN_ID_THREE,
			COOKIES.LEGACY_CAMPAIGN_ID_TWO,
			COOKIES.LEGACY_CAMPAIGN_ID,
		];

		let legacySourceTag = null;
		for (const cookieName of legacyCookiesNames) {
			const cookieValue = Cookies.get(cookieName);
			if (cookieValue) {
				legacySourceTag = cookieValue;
				break;
			}
		}
		const cachedSourceTag = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);

		if (sourceTag) {
			const queryParams = {};
			for (const [key, value] of searchParams.entries()) {
				queryParams[key] = value;
			}

			delete queryParams.src_tag;
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.SOURCE_INFO,
				JSON.stringify({
					id: sourceTag,
					values: queryParams,
				})
			);
			return sourceTag;
		}

		if (legacySourceTag) {
			let sourceInfo = {};
			const dynamicInfoLegacy = Cookies.get(COOKIES.LEGACY_CAMPAIGN_VARS);

			if (dynamicInfoLegacy) {
				try {
					sourceInfo = JSON.parse(decodeURIComponent(dynamicInfoLegacy));
				} catch (err) {
					Sentry.captureException(err, {
						extra: {
							action: 'Error: Decoding Legacy Source Tag',
						},
					});
				}
			}
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.SOURCE_INFO,
				JSON.stringify({
					id: legacySourceTag,
					values: sourceInfo,
				})
			);
			return legacySourceTag;
		}

		try {
			const tag = JSON.parse(cachedSourceTag);
			return tag?.id || null;
		} catch (err) {
			return cachedSourceTag || null;
		}
	}

	/**
	 * Attaches a tracking pixel to the document by creating an iframe and writing the pixel content into it.
	 * @param {string} pixel - The HTML content of the tracking pixel to be attached.
	 * @returns {Promise<void>} A promise that resolves when the iframe has successfully loaded the pixel.
	 */
	static attachPixel(pixel) {
		return new Promise((resolve, _) => {
			const iFrame = document.createElement('iframe');
			iFrame.width = 1;
			iFrame.height = 1;
			iFrame.loading = 'lazy';
			iFrame.fetchpriority = 'low';
			iFrame.frameborder = 0;
			iFrame.style.display = 'none';

			document.body.appendChild(iFrame);

			iFrame.addEventListener('load', function () {
				resolve();
			});

			iFrame.contentWindow.document.open();
			iFrame.contentWindow.document.write(pixel);
			iFrame.contentWindow.document.close();
		});
	}

	/**
	 * Fires a campaign pixel if the global privacy control is not enabled.
	 * Retrieves campaign information from local storage and sends it to the API service.
	 * If a pixel is returned, it tracks and attaches the pixel.
	 * @param {string} event - The event name to be tracked.
	 * @returns {Promise<void>} - A promise that resolves when the pixel has been processed.
	 * @throws Will capture and log any exceptions using Sentry.
	 */
	static async fireCampaignPixel(event) {
		if (
			(typeof navigator !== 'undefined' && navigator.globalPrivacyControl) ||
			get(app)?.meta?.inApp
		)
			return;
		const campaignInfo = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);
		if (campaignInfo) {
			const payload = {
				event,
				campaignInfo: JSON.parse(campaignInfo),
			};

			try {
				const response = await ApiService.resolvePixelInfo(payload);
				if (response && response.pixel) {
					await AnalyticsService.trackPixel('before', event, 'campaign');
					await AnalyticsService.attachPixel(response.pixel);
					await AnalyticsService.trackPixel('after', event, 'campaign');
				}
			} catch (e) {
				Sentry.captureException(e, {
					extra: {
						action: 'Error: Fire Campaign Pixel',
					},
				});
			}
		}
	}

	/**
	 * Tracks a pixel event by sending a payload to the analytics service.
	 * @param {string} action - The action associated with the pixel event.
	 * @param {string} event - The specific event to be tracked.
	 * @param {string} type - The type of the event.
	 * @returns {Promise<object>} - A promise that resolves when the event is successfully fired.
	 */
	static async trackPixel(action, event, type) {
		const campaignId = AnalyticsService.getSourceTag();

		const payload = {
			event_type: `${EVENT_NAMES.PIXEL_FIRE}-${action}`,
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				event,
				type,
				campaignId,
			},
		};

		return await AnalyticsService.fireEvent({ event: payload });
	}

	/**
	 * Attribute the source tag to a campaign click event.
	 * @param {object|string} sourceTag - The source tag
	 * @returns {Promise<object>} - A promise that resolves when the event is tracked.
	 */
	static async attributeSource(sourceTag) {
		const campaignInfo = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);
		const dynamicInfo = campaignInfo ? JSON.parse(campaignInfo).values : {};
		const payload = {
			event_type: EVENT_NAMES.CAMPAIGN_CLICK,
			device_id: getDeviceId(),
			event_payload: {
				aofl_product: ProductExperienceService.AOFL_PRODUCT_NAME,
				source_info: {
					source_tag: sourceTag,
					dynamic_info: { ...dynamicInfo },
				},
			},
			udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
			adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
		};

		return await AnalyticsService.fireEvent({ event: payload });
	}

	/**
	 * Tracks an event using the analytics service.
	 * @param {object} payload - The payload to track.
	 * @returns {Promise<object>} A promise that resolves when the event is tracked.
	 */
	static async fireEvent(payload) {
		try {
			if (payload?.event?.event_payload) {
				payload.event.event_payload.app_version = __APP_VERSION__; //eslint-disable-line
			}

			return await ApiService.eventLog(payload);
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					action: 'Error: Fire Event Log',
				},
			});
		}
	}

	static setCJCookie() {
		const searchParams = new URLSearchParams(location.search);
		const cjEvent = searchParams.get(QUERY_PARAM_KEYS.CJ_EVENT);
		if (cjEvent) {
			Cookies.set(COOKIES.CJ_COOKIE, cjEvent, COOKIES.CJ_CONFIG);
		}
	}

	/**
	 * Gets first visible element from provided container in viewport
	 * @param {string} containerSelector - the container to check children visibility
	 * @param {number} buffer - the amount of buffer to determine if element is in view
	 * @returns {(Element|null)} - The found element, or null if not found.
	 */
	static getFirstVisibleElement(containerSelector, buffer) {
		const container = document.querySelector(containerSelector);
		if (!container) {
			return null;
		}

		const items = container.children;
		let containerTop = window.scrollY + buffer;

		for (const item of items) {
			const itemBottom = item.offsetTop + item.offsetHeight - buffer;
			if (itemBottom > containerTop) {
				return item;
			}
		}

		return null;
	}

	/**
	 * Handles actions to be performed after navigation.
	 * @param {object} options - The options for the analytics
	 * @param {string} options.componentName - The name of the component
	 */
	static async afterNavigate({ componentName }) {
		ProductExperienceService.setProductExperienceFromQuery();
		AnalyticsService.thirdPartyPageLoad();
		await AnalyticsService.trackPageView({ componentName });
		await AnalyticsService.dataLayerPush({ eventName: 'global' });
	}

	/**
	 * Handles actions to be performed when the component is mounted.
	 */
	static async onMount() {
		AnalyticsService.setCJCookie();
		ProductExperienceService.setProductExperienceFromQuery();
		setAllAnalyticsIdentifiersFromQuery();
		const sourceTag = AnalyticsService.getSourceTag();
		if (sourceTag) {
			Sentry.setTag('source', sourceTag);
			await AnalyticsService.attributeSource(sourceTag);
		}
		AnalyticsService.setClickTrackingListener();
		await AnalyticsService.trackEmailClick();
	}

	/**
	 * Handles actions to be performed when the component is destroyed.
	 */
	static async onDestroy() {
		AnalyticsService.removeClickTrackingListener();
	}
}

export { AnalyticsService };
